import React, { useState } from "react";
import {
  ScrollView,
  View,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import {
  Button,
  Text,
  useTheme,
  IconButton,
  Searchbar,
  Menu,
} from "react-native-paper";
import { useTranslation } from "react-i18next";
import { DatePickerModal } from "react-native-paper-dates";
import { CalendarDate } from "react-native-paper-dates/lib/typescript/Date/Calendar";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { Filter, Filters } from "../config/companies/config";

interface FilterBarProps {
  filters: Filters;
  onFilterChange: (filters: Filters) => void;
  activeFilters: Filters;
}

export default function FilterBar({
  filters,
  onFilterChange,
  activeFilters,
}: FilterBarProps) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState<{
    startDate: CalendarDate;
    endDate: CalendarDate;
  }>({
    startDate: undefined,
    endDate: undefined,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [openMenuField, setOpenMenuField] = useState<string | null>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const { height: windowHeight } = useWindowDimensions();
  const dropdownMenuMaxHeight = Math.max(
    180,
    Math.min(360, windowHeight * 0.55),
  );

  const onResetDateFilter = () => {
    setDateRange({
      startDate: undefined,
      endDate: undefined,
    });

    const updatedFilters = activeFilters.map((filter) => {
      if (filter.type === "date") {
        return { ...filter, value: undefined };
      }
      return filter;
    });

    onFilterChange(updatedFilters);
  };

  const onConfirm = React.useCallback(
    (range: { startDate: CalendarDate; endDate: CalendarDate }) => {
      setShowDatePicker(false);
      const { startDate, endDate } = range;
      if (startDate && endDate) {
        setDateRange(range);

        const updatedFilters = activeFilters.map((filter) => {
          if (filter.type === "date") {
            return {
              ...filter,
              value: {
                startDate,
                endDate,
              },
            };
          }
          return filter;
        });

        onFilterChange(updatedFilters);
      }
    },
    [onFilterChange, activeFilters],
  );

  const onChangeSearch = (text: string, filter: Filter) => {
    setSearchTerm(text);
    const updatedFilters = activeFilters.map((f) => {
      if (f.type === "search" && f.field === filter.field) {
        return { ...f, value: text };
      }
      return f;
    });
    onFilterChange(updatedFilters);
  };

  const onDropdownSelect = (filter: Filter, value: string) => {
    const updatedFilters = activeFilters.map((f) => {
      if (f.type === "dropdown" && f.field === filter.field) {
        return { ...f, value };
      }
      return f;
    });

    onFilterChange(updatedFilters);
    setOpenMenuField(null);
  };

  const getFilterLabel = (filter: Filter) => {
    const activeFilter = activeFilters.find((f) => f.field === filter.field);
    if (activeFilter?.type === "date" && activeFilter.value) {
      const { startDate, endDate } = activeFilter.value;
      if (startDate && endDate) {
        return `${format(startDate, "dd.MM.yyyy", { locale: ru })} - ${format(endDate, "dd.MM.yyyy", { locale: ru })}`;
      }
    }
    if (activeFilter?.type === "dropdown" && activeFilter.value) {
      return t(activeFilter.value);
    }
    return t(filter.label || filter.field);
  };

  const renderFilter = (filter: Filter) => {
    switch (filter.type) {
      case "date": {
        const activeDateFilter = activeFilters.find(
          (f) => f.type === "date" && f.field === filter.field,
        );
        return (
          <Button
            key={filter.field}
            mode="outlined"
            onPress={() => setShowDatePicker(true)}
            style={[
              styles.filterButton,
              activeDateFilter?.value && {
                backgroundColor: theme.colors.primaryContainer,
              },
            ]}
            icon="calendar"
          >
            {getFilterLabel(filter)}
          </Button>
        );
      }
      case "search":
        return (
          <Searchbar
            key={filter.field}
            value={searchTerm}
            onChangeText={(t: string) => onChangeSearch(t, filter)}
            placeholder={t(filter.label)}
            style={[styles.searchBar, { width: "100%" }]}
            iconColor="#0f172a"
            elevation={0}
          />
        );
      case "dropdown":
        return (
          <Menu
            key={filter.field}
            visible={openMenuField === filter.field}
            onDismiss={() => setOpenMenuField(null)}
            anchor={
              <Button
                mode="outlined"
                onPress={() => setOpenMenuField(filter.field)}
                style={styles.filterButton}
                icon="filter-variant"
              >
                {`${t(filter.label)}: ${getFilterLabel(filter)}`}
              </Button>
            }
          >
            <ScrollView
              style={{ maxHeight: dropdownMenuMaxHeight }}
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled
            >
              <Menu.Item
                onPress={() => onDropdownSelect(filter, "")}
                title={t("All")}
              />
              {(filter.options || []).map((option) => (
                <Menu.Item
                  key={option}
                  onPress={() => onDropdownSelect(filter, option)}
                  title={t(option)}
                />
              ))}
            </ScrollView>
          </Menu>
        );

      default:
        return <Text>Нужно настроит этот фильтр в FilterBar.tsx</Text>;
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.searchRow}>
        {filters.filter((f) => f.type === "search").map(renderFilter)}
      </View>

      <View style={styles.filtersRow}>
        {filters.filter((f) => f.type !== "search").map(renderFilter)}
        {dateRange.startDate && dateRange.endDate && (
          <IconButton
            icon="close"
            size={20}
            mode="contained"
            onPress={onResetDateFilter}
          />
        )}
      </View>

      <DatePickerModal
        mode="range"
        onConfirm={onConfirm}
        visible={showDatePicker}
        locale={"ru"}
        onDismiss={() => setShowDatePicker(false)}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        startWeekOnMonday={true}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    alignItems: "stretch",
    rowGap: 8,
    padding: 16,
  },
  searchRow: {
    width: "100%",
  },
  filtersRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  filterButton: {
    borderRadius: 8,
  },
  searchBar: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#dbe2ea",
    backgroundColor: "#ffffff",
  },
});
